// Health & ops view (wireframe 11) — service-health cards + smoke test
// summary + verify-state summary + recent operations timeline. Right pin
// pane holds backups + prerequisites.

import { useMemo } from "react";
import { useShell, useRightPane } from "../components/ShellContext.js";
import { useTool } from "../lib/useTool.js";
import { ErrorState, Skeleton } from "./common.js";

interface HealthRow {
  kind: string;
  name: string;
  status: string;
  details: string;
}

interface BackupRow {
  app: string;
  filename: string;
  size_bytes: number;
  mtime_iso: string;
  path: string;
}

export default function HealthView() {
  const { openChat } = useShell();
  const health = useTool("health_check", {});
  const smoke = useTool("smoke_test", {});
  const verify = useTool("verify_state", {});
  const backups = useTool("list_backups", {});
  const changes = useTool("list_canonical_changes", { limit: 6 });

  const healthRows = ((health.env?.data as { rows?: HealthRow[] } | undefined)?.rows ?? []) as HealthRow[];
  const smokeData = (smoke.env?.data as { rows?: any[] } | string | undefined);
  const verifyText = useMemo(() => {
    const d = verify.env?.data;
    return typeof d === "string" ? d : JSON.stringify(d ?? "", null, 2);
  }, [verify.env]);
  const backupRows = ((backups.env?.data as { rows?: BackupRow[] } | undefined)?.rows ?? []) as BackupRow[];
  const commits = ((changes.env?.data as { rows?: Array<{ short: string; subject: string; date: string }> } | undefined)?.rows ?? []);

  // Group health rows by service kind for the 4-card row at the top.
  const services = useMemo(() => groupServices(healthRows), [healthRows]);

  // Parse smoke output into pass/fail counts.
  const smokeSummary = useMemo(() => summarizeSmoke(smokeData), [smokeData]);
  const verifySummary = useMemo(() => summarizeVerify(verifyText), [verifyText]);

  useRightPane(
    useMemo(() => (
      <RightPane backups={backupRows} openChat={openChat} />
    ), [backupRows, openChat]),
  );

  return (
    <div className="space-y-3">
      {/* Action buttons */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {services.length} service{services.length === 1 ? "" : "s"} probed
          {smokeSummary && ` · smoke ${smokeSummary.pass}/${smokeSummary.total}`}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button type="button"
                  onClick={() => openChat("Run the smoke test suite against all six apps (the three web apps OpenProject/LibrePlan/ProjeQtOr plus the three static renderers Markmap/PlantUML/TaskJuggler).")}
                  className="rounded border border-slate-200 bg-white px-2 py-1 hover:bg-slate-100">
            Run smoke test
          </button>
          <button type="button"
                  onClick={() => openChat("Backup all six apps now (OpenProject, LibrePlan, ProjeQtOr web apps + Markmap, PlantUML, TaskJuggler renderers).")}
                  className="rounded border border-slate-200 bg-white px-2 py-1 hover:bg-slate-100">
            Backup all
          </button>
          <button type="button"
                  onClick={() => openChat("Restore the most recent backup. List the candidate files first.")}
                  className="rounded border border-slate-200 bg-white px-2 py-1 hover:bg-slate-100">
            Restore…
          </button>
        </div>
      </div>

      {/* Service-health cards */}
      <section className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4">
        {services.length === 0 ? (
          <div className="col-span-full rounded-lg border border-dashed border-slate-200 bg-white p-4 text-xs text-slate-500">
            {health.loading ? "Probing services…" :
             health.error ? `Health check failed: ${health.error}` :
             "No probes yet. Click Run smoke test to populate."}
          </div>
        ) : services.map((s) => (
          <ServiceCard key={s.name} {...s} />
        ))}
      </section>

      {/* Smoke + Verify summary cards */}
      <section className="grid grid-cols-1 gap-2 lg:grid-cols-2">
        <SummaryCard
          title="Smoke test"
          subtitle={smoke.loading ? "running…" : smoke.error ? smoke.error : "latest run"}
          chip={smokeSummary
            ? { label: `${smokeSummary.pass} / ${smokeSummary.total} pass`,
                tone: smokeSummary.pass === smokeSummary.total ? "ok" : "warn" }
            : undefined}
          rows={smokeSummary?.rows ?? []}
          columns={["check", "expects", "status", "ms"]}
        />
        <SummaryCard
          title="Verify state"
          subtitle={verify.loading ? "running…" : verify.error ? verify.error : "latest run"}
          chip={verifySummary
            ? { label: `${verifySummary.ok} ok · ${verifySummary.warn} warn · ${verifySummary.fail} fail`,
                tone: verifySummary.fail > 0 ? "fail" : verifySummary.warn > 0 ? "warn" : "ok" }
            : undefined}
          rows={verifySummary?.rows ?? []}
          columns={["row", "detail", "status"]}
        />
      </section>

      {/* Recent operations */}
      <section className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="mb-2 text-sm font-medium text-slate-700">Recent operations</div>
        {changes.loading ? <Skeleton lines={3} /> :
         changes.error ? <ErrorState error={changes.error} onRetry={changes.refetch} /> :
         commits.length === 0 ? (
           <div className="text-xs text-slate-500">No recent operations in git log.</div>
         ) : (
           <ol className="space-y-1.5 border-l border-slate-200 pl-4 text-xs">
             {commits.map((c) => (
               <li key={c.short} className="relative">
                 <span className="absolute -left-[7px] inline-block h-2.5 w-2.5 rounded-full bg-sky-500" />
                 <span className="text-slate-500">
                   {c.date?.slice(0, 10) ?? ""} · <span className="font-mono">{c.short}</span>
                 </span>
                 {" — "}{c.subject}
               </li>
             ))}
           </ol>
         )}
      </section>
    </div>
  );
}

function ServiceCard({ name, status, details, statusTone }: {
  name: string;
  status: string;
  details: string;
  statusTone: "ok" | "warn" | "fail" | "unknown";
}) {
  const dotColor =
    statusTone === "ok"   ? "bg-emerald-500" :
    statusTone === "warn" ? "bg-amber-500" :
    statusTone === "fail" ? "bg-rose-500" :
                            "bg-slate-300";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-2">
        <span className={`h-2.5 w-2.5 rounded-full ${dotColor}`} />
        <span className="text-sm font-medium text-slate-900">{name}</span>
      </div>
      <div className="mt-1 text-[11px] text-slate-500">{details || "—"}</div>
      <div className="mt-1 text-[10px] font-mono text-slate-400">{status}</div>
    </div>
  );
}

function SummaryCard({ title, subtitle, chip, rows, columns }: {
  title: string;
  subtitle: string;
  chip?: { label: string; tone: "ok" | "warn" | "fail" };
  rows: Array<Record<string, string>>;
  columns: string[];
}) {
  const chipClass =
    chip?.tone === "ok"   ? "bg-emerald-100 text-emerald-700" :
    chip?.tone === "warn" ? "bg-amber-100 text-amber-700" :
    chip?.tone === "fail" ? "bg-rose-100 text-rose-700" :
                            "bg-slate-100 text-slate-700";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-slate-700">{title}</div>
          <div className="text-[11px] text-slate-500">{subtitle}</div>
        </div>
        {chip && (
          <span className={`rounded px-2 py-0.5 text-[11px] ${chipClass}`}>{chip.label}</span>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500">No rows.</div>
      ) : (
        <table className="w-full text-[11px]">
          <thead className="text-slate-500">
            <tr className="border-b border-slate-100">
              {columns.map((c) => <th key={c} className="text-left font-normal">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 8).map((r, i) => (
              <tr key={i} className="border-b border-slate-50 last:border-b-0">
                {columns.map((c) => (
                  <td key={c} className="py-1 pr-2 align-top">
                    {c === "status" ? renderStatusCell(r[c]) : r[c] ?? ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rows.length > 8 && (
        <div className="mt-1 text-[10px] text-slate-400">… {rows.length - 8} more</div>
      )}
    </div>
  );
}

function renderStatusCell(s: string | undefined): React.ReactNode {
  const u = (s ?? "").toUpperCase();
  if (u === "PASS" || u === "OK") return <span className="text-emerald-600">✓</span>;
  if (u === "WARN") return <span className="text-amber-600">warn</span>;
  if (u === "FAIL" || u === "ERROR" || u === "DOWN") return <span className="text-rose-600">✗</span>;
  return s ?? "";
}

function RightPane({ backups, openChat }: {
  backups: BackupRow[];
  openChat: (prefill?: string) => void;
}) {
  const latestPerApp = useMemo(() => {
    const seen = new Set<string>();
    const out: BackupRow[] = [];
    for (const b of backups) {
      if (seen.has(b.app)) continue;
      seen.add(b.app);
      out.push(b);
    }
    return out;
  }, [backups]);

  return (
    <div className="space-y-4">
      <section>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Backups</div>
        <ul className="mt-2 space-y-1 rounded border border-slate-200 p-2 text-xs">
          {latestPerApp.length === 0 ? (
            <li className="text-slate-500">No backups in <code>_backups/</code>.</li>
          ) : latestPerApp.map((b) => (
            <li key={b.app} className="flex items-center justify-between">
              <span className="text-slate-700 capitalize">{b.app}</span>
              <span className="font-mono text-[10px] text-slate-500">
                {agoIso(b.mtime_iso)}
              </span>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => openChat("Run backup_all to snapshot every app database.")}
          className="mt-2 w-full rounded border border-slate-200 bg-slate-900 px-2 py-1 text-xs text-white hover:bg-slate-700"
        >Run backup_all</button>
      </section>

      <section>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Prerequisites</div>
        <ul className="mt-2 space-y-1 rounded border border-slate-200 p-2 text-xs text-slate-500">
          <li>Run <button type="button" onClick={() => openChat("Show the output of bin/check_prereqs.sh.")} className="font-mono text-sky-700 hover:underline">bin/check_prereqs.sh</button> for the live versions of TaskJuggler, PlantUML, Markmap CLI, LibreOffice, Colima.</li>
        </ul>
      </section>
    </div>
  );
}

function agoIso(iso: string): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const secs = Math.max(0, (Date.now() - t) / 1000);
  if (secs < 60) return `${Math.round(secs)}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

// ---- Grouping + parsing helpers ----------------------------------------

interface ServiceCardData {
  name: string;
  status: string;
  details: string;
  statusTone: "ok" | "warn" | "fail" | "unknown";
}

function groupServices(rows: HealthRow[]): ServiceCardData[] {
  // Each health-check row has kind/name/status/details. Group by name so a
  // service produces a single card with the worst probe status.
  const byName = new Map<string, HealthRow[]>();
  for (const r of rows) {
    const key = r.name || r.kind;
    if (!byName.has(key)) byName.set(key, []);
    byName.get(key)!.push(r);
  }
  const out: ServiceCardData[] = [];
  byName.forEach((probes, name) => {
    const statuses = probes.map((p) => p.status?.toUpperCase());
    const tone: ServiceCardData["statusTone"] =
      statuses.some((s) => /FAIL|DOWN|ERROR/.test(s)) ? "fail" :
      statuses.some((s) => /WARN|SLOW/.test(s))      ? "warn" :
      statuses.some((s) => /OK|PASS|UP/.test(s))     ? "ok"   :
                                                       "unknown";
    out.push({
      name,
      status: statuses.join(", "),
      details: probes.map((p) => p.details).filter(Boolean).join(" · "),
      statusTone: tone,
    });
  });
  return out;
}

function summarizeSmoke(data: any): {
  rows: Array<Record<string, string>>;
  pass: number;
  total: number;
} | null {
  if (!data) return null;
  if (typeof data === "string") return null;
  const rows = (data.rows ?? []) as Array<{
    name: string; status: string; details?: string; ms?: number;
  }>;
  if (!Array.isArray(rows)) return null;
  const pass = rows.filter((r) => /PASS|OK/i.test(r.status ?? "")).length;
  return {
    pass,
    total: rows.length,
    rows: rows.map((r) => ({
      check: r.name,
      expects: r.details ?? "",
      status: r.status,
      ms: r.ms == null ? "" : String(r.ms),
    })),
  };
}

function summarizeVerify(text: string): {
  rows: Array<Record<string, string>>;
  ok: number;
  warn: number;
  fail: number;
} | null {
  if (!text) return null;
  const rows: Array<Record<string, string>> = [];
  let ok = 0, warn = 0, fail = 0;
  for (const line of text.split(/\n+/)) {
    const m = line.match(/^\s*\[\s*([^\]]+)\s*\]\s*([✓×✗⚠])\s*(.+?)(?:\s*=\s*(.+))?$/);
    if (!m) continue;
    const section = m[1].trim();
    const sym = m[2];
    const label = m[3].trim();
    const detail = (m[4] ?? "").trim();
    const status = sym === "✓" ? "ok" : sym === "⚠" ? "warn" : "fail";
    if (status === "ok") ok += 1;
    else if (status === "warn") warn += 1;
    else fail += 1;
    rows.push({ row: `${section} · ${label}`, detail, status: status.toUpperCase() });
  }
  if (rows.length === 0) return null;
  return { rows, ok, warn, fail };
}
